
import React, { useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import Layout from "@/components/layout/Layout";
import { Lock, Sparkles, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";
import { useSupabase } from "@/hooks/useSupabase";
import { ArticleInteractions } from "@/components/articles/ArticleInteractions";
import { ArticleComments } from "@/components/articles/ArticleComments";
import { mockArticles } from "@/utils/mockArticles";
import type { Article } from "@/types/article";

const ArticlePage: React.FC = () => {
  const { id } = useParams();
  const { hasPremiumAccess, isCheckingAccess } = useSubscription();
  const { user, isLoggedIn } = useSupabase();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Find the article in mockArticles instead of the hardcoded allArticles array
  const article = mockArticles.find(a => a.id === Number(id));
  
  useEffect(() => {
    // Scroll to top when component mounts or article ID changes
    window.scrollTo(0, 0);
    
    console.log(`Article page for ID ${id}`);
    console.log(`User logged in: ${isLoggedIn}, Has premium access: ${hasPremiumAccess}, Is checking access: ${isCheckingAccess}`);
    
    if (article && article.premium && !isLoggedIn) {
      console.log('No user logged in for premium article, redirecting to login');
      toast({
        title: "Login Required",
        description: "Please login to access premium content"
      });
      navigate('/login', { state: { returnTo: `/article/${id}` } });
    }
  }, [id, isLoggedIn, article, toast, navigate, hasPremiumAccess, isCheckingAccess]);
  
  if (!article) {
    return <Navigate to="/articles" replace />;
  }

  if (isLoggedIn && article.premium && isCheckingAccess) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <p>Checking subscription status...</p>
        </div>
      </Layout>
    );
  }

  const canAccessContent = !article.premium || isLoggedIn;

  return (
    <Layout>
      <article className="max-w-4xl mx-auto px-4 py-12">
        <div className="space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="space-y-2 flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{article.category}</span>
                {article.premium && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-premium text-premium-foreground">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Premium
                  </span>
                )}
              </div>
              <h1 className="text-4xl font-bold text-gray-900">{article.title}</h1>
              <p className="text-gray-500">{article.readTime} • {article.date}</p>
            </div>
            
            <div className="flex flex-row items-center gap-1 p-2 min-w-fit bg-muted/50 border border-gray-100 rounded-xl shadow-sm ml-auto">
              <div className="flex items-center text-gray-400 text-xs px-2">
                <Eye className="h-4 w-4 mr-1" />
                <span className="text-gray-600">{article.views ?? 0}</span>
                <span className="ml-1">views</span>
              </div>
              <div className="flex gap-1">
                <ArticleInteractions articleId={article.id.toString()} />
              </div>
            </div>
          </div>

          <div className="aspect-video w-full overflow-hidden rounded-lg">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b";
              }}
            />
          </div>

          {canAccessContent ? (
            <div className="prose max-w-none">
              {article.content?.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4 text-gray-600 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 space-y-4">
              <Lock className="w-12 h-12 mx-auto text-gray-400" />
              <h2 className="text-2xl font-semibold text-gray-900">Premium Content</h2>
              <p className="text-gray-600 max-w-md mx-auto">
                {isLoggedIn ? 
                  "Upgrade your account to access premium content." : 
                  "This article is available exclusively to our premium subscribers."}
              </p>
              <Link to="/pricing">
                <Button>
                  {isLoggedIn ? "Upgrade to Premium" : "Subscribe Now"}
                </Button>
              </Link>
            </div>
          )}
        </div>

        {canAccessContent && (
          <ArticleComments articleId={id || ""} />
        )}
      </article>
    </Layout>
  );
};

export default ArticlePage;
